import { useEffect, useMemo, useState } from "react";
import { Database, Download, Filter, Loader2, Search } from "lucide-react";
import { PageHeader, Panel, Overline } from "@/components/Panel";
import {
  downloadAdminExplorerMeasurementsCsv,
  getAdminExplorerCustomers,
  getAdminExplorerDevices,
  getAdminExplorerMeasurements,
  getAdminExplorerMetrics,
  getAdminTenantFields,
  getAdminTenants,
} from "@/api/treeplet";

const inputClass = "w-full h-10 rounded-md border border-line bg-surface px-3 text-sm outline-none focus:border-brand disabled:cursor-not-allowed disabled:bg-page";
const labelClass = "mb-1 block text-[10px] font-semibold uppercase tracking-wider text-ink-secondary";
const PAGE_SIZE = 100;
const EMPTY_RESULTS = { data: [], next_offset: null, metrics: [], mode: null, interval: null };
const METRIC_LABELS = {
  leaf_surface_temperature_c: "Temperatura de hoja (°C)",
  leaf_surface_humidity_pct: "Humedad de hoja (%)",
  air_temperature_c: "Temperatura del aire (°C)",
  air_humidity_pct: "Humedad del aire (%)",
};

const toUtc = (value) => value ? new Date(value).toISOString() : undefined;
const errorText = (error) => error?.detail || error?.message || "No se pudieron cargar los datos.";
const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "number") return Number.isInteger(value) ? value.toLocaleString("es-ES") : value.toLocaleString("es-ES", { maximumFractionDigits: 3 });
  if (typeof value === "string" && /^\d{4}-\d\d-\d\dT/.test(value)) {
    const date = new Date(value);
    if (!Number.isNaN(date.valueOf())) return new Intl.DateTimeFormat("es-ES", { dateStyle: "short", timeStyle: "medium" }).format(date);
  }
  return String(value);
};
const columnLabel = (column) => {
  const suffix = column.match(/_(avg|min|max|count)$/)?.[1];
  const metricKey = suffix ? column.slice(0, -(suffix.length + 1)) : column;
  return `${METRIC_LABELS[metricKey] || metricKey.replaceAll("_", " ")}${suffix ? ` · ${suffix}` : ""}`;
};

function ResultsTable({ data, loading, error, nextOffset, truncated, onLoadMore, loadingMore, emptyText }) {
  const columns = useMemo(() => data.length ? Object.keys(data[0]) : [], [data]);
  return (
    <Panel className="overflow-hidden" testId="admin-explorer-results">
      {loading ? <div className="flex min-h-40 items-center justify-center gap-2 text-sm text-ink-secondary"><Loader2 size={18} className="animate-spin text-brand" /> Consultando…</div>
        : error ? <div className="p-5 text-sm text-critical">{error}</div>
          : !data.length ? <div className="p-6 text-sm text-ink-secondary">{emptyText}</div>
            : <>
              <div className="flex items-center justify-between border-b border-line px-4 py-3 text-xs text-ink-secondary">
                <span>{data.length.toLocaleString("es-ES")} filas cargadas</span>
                <span>Las fechas se muestran en la zona horaria local del navegador.</span>
              </div>
              <div className="overflow-x-auto tp-scroll">
                <table className="w-full min-w-max text-left text-xs">
                  <thead className="bg-page text-[10px] uppercase tracking-wide text-ink-tertiary"><tr>{columns.map((column) => <th key={column} className="whitespace-nowrap px-3 py-3 font-semibold">{columnLabel(column)}</th>)}</tr></thead>
                  <tbody className="divide-y divide-line">{data.map((row, rowIndex) => <tr key={`${row.message_id || row.bucket_start || row.id || row.device_id}-${rowIndex}`} className="hover:bg-page/70">{columns.map((column) => <td key={column} className="whitespace-nowrap px-3 py-2.5 text-ink-secondary">{displayValue(row[column])}</td>)}</tr>)}</tbody>
                </table>
              </div>
              {(nextOffset !== null || truncated) && <div className="border-t border-line p-3 text-center">{truncated && <p className="mb-2 text-xs text-ink-secondary">Se alcanzó el límite de 50.000 filas. Reduce el rango o el ámbito para ver más.</p>}{nextOffset !== null && <button type="button" disabled={loadingMore} onClick={onLoadMore} className="inline-flex h-9 items-center gap-2 rounded-md border border-line px-4 text-xs font-semibold text-brand disabled:opacity-50">{loadingMore && <Loader2 size={14} className="animate-spin" />}Cargar más</button>}</div>}
            </>}
    </Panel>
  );
}

export default function AdminDataExplorer() {
  const [dataset, setDataset] = useState("measurements");
  const [tenants, setTenants] = useState([]);
  const [fields, setFields] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [lookupError, setLookupError] = useState("");
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [customerFilters, setCustomerFilters] = useState({ search: "", minDevicesGt: "", countScope: "all" });
  const [deviceFilters, setDeviceFilters] = useState({ tenantId: "", fieldId: "", lifecycleState: "", search: "" });
  const [measurementFilters, setMeasurementFilters] = useState({ tenantId: "", fieldId: "", deviceId: "", from: "", to: "", mode: "sampled", interval: "10m", groupBy: "device", quality: "valid", metrics: [] });
  const [applied, setApplied] = useState(null);
  const [results, setResults] = useState(EMPTY_RESULTS);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    Promise.all([getAdminTenants(), getAdminExplorerMetrics()])
      .then(([tenantEnvelope, metricEnvelope]) => {
        if (!active) return;
        setTenants(tenantEnvelope.data || []);
        const metricList = metricEnvelope.data || [];
        setMetrics(metricList);
        setMeasurementFilters((current) => ({ ...current, metrics: metricList.map((metric) => metric.key) }));
      })
      .catch((requestError) => active && setLookupError(errorText(requestError)));
    return () => { active = false; };
  }, []);

  const selectedTenantId = dataset === "devices" ? deviceFilters.tenantId : measurementFilters.tenantId;
  useEffect(() => {
    let active = true;
    if (!selectedTenantId) {
      setFields([]);
      setFieldsLoading(false);
      return () => { active = false; };
    }
    setFieldsLoading(true);
    getAdminTenantFields(selectedTenantId)
      .then((envelope) => active && setFields(envelope.data || []))
      .catch((requestError) => active && setLookupError(errorText(requestError)))
      .finally(() => active && setFieldsLoading(false));
    return () => { active = false; };
  }, [selectedTenantId]);

  const requestFor = (kind, filters, offset = 0) => {
    if (kind === "customers") return getAdminExplorerCustomers({
      search: filters.search || undefined,
      min_devices_gt: filters.minDevicesGt === "" ? undefined : Number(filters.minDevicesGt),
      count_scope: filters.countScope,
      limit: PAGE_SIZE, offset,
    });
    if (kind === "devices") return getAdminExplorerDevices({
      tenant_id: filters.tenantId || undefined,
      field_id: filters.fieldId || undefined,
      lifecycle_state: filters.lifecycleState || undefined,
      search: filters.search || undefined,
      limit: PAGE_SIZE, offset,
    });
    return getAdminExplorerMeasurements({
      tenant_id: filters.tenantId,
      field_id: filters.fieldId || undefined,
      device_id: filters.deviceId || undefined,
      from: toUtc(filters.from), to: toUtc(filters.to),
      mode: filters.mode, interval: filters.interval,
      group_by: filters.mode === "aggregate" ? filters.groupBy : "device",
      quality: filters.mode === "aggregate" ? "valid" : filters.quality,
      metrics: filters.metrics.join(","), limit: PAGE_SIZE, offset,
    });
  };

  const runQuery = async (event) => {
    event?.preventDefault();
    const kind = dataset;
    const filters = kind === "customers" ? { ...customerFilters } : kind === "devices" ? { ...deviceFilters } : { ...measurementFilters };
    if (kind === "measurements" && (!filters.tenantId || (!filters.fieldId && !filters.deviceId) || !filters.from || !filters.to || !filters.metrics.length)) {
      setError("Selecciona cliente, parcela o dispositivo, intervalo de fechas y al menos una medición.");
      return;
    }
    if (kind === "measurements" && new Date(filters.to) <= new Date(filters.from)) {
      setError("La fecha final debe ser posterior a la fecha inicial.");
      return;
    }
    setApplied({ kind, filters });
    setResults(EMPTY_RESULTS);
    setError("");
    setLoading(true);
    try {
      const response = await requestFor(kind, filters, 0);
      setResults(response);
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!applied || results.next_offset === null) return;
    setLoadingMore(true);
    setError("");
    try {
      const response = await requestFor(applied.kind, applied.filters, results.next_offset);
      setResults((current) => ({ ...response, data: [...current.data, ...(response.data || [])] }));
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setLoadingMore(false);
    }
  };

  const exportCsv = async () => {
    if (!applied || applied.kind !== "measurements") return;
    setExporting(true);
    setError("");
    const filters = applied.filters;
    try {
      const blob = await downloadAdminExplorerMeasurementsCsv({
        tenant_id: filters.tenantId, field_id: filters.fieldId || undefined, device_id: filters.deviceId || undefined,
        from: toUtc(filters.from), to: toUtc(filters.to), mode: filters.mode, interval: filters.interval,
        group_by: filters.mode === "aggregate" ? filters.groupBy : "device",
        quality: filters.mode === "aggregate" ? "valid" : filters.quality,
        metrics: filters.metrics.join(","),
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "treeplet-measurements.csv";
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setExporting(false);
    }
  };

  const selectDataset = (nextDataset) => {
    setDataset(nextDataset);
    setApplied(null);
    setResults(EMPTY_RESULTS);
    setError("");
  };

  const updateMeasurement = (event) => {
    const { name, value } = event.target;
    setMeasurementFilters((current) => ({
      ...current,
      [name]: value,
      ...(name === "tenantId" ? { fieldId: "", deviceId: "", groupBy: "device" } : {}),
      ...(name === "fieldId" ? { deviceId: "", ...(value ? {} : { groupBy: "device" }) } : {}),
    }));
  };
  const toggleMetric = (key) => setMeasurementFilters((current) => ({ ...current, metrics: current.metrics.includes(key) ? current.metrics.filter((item) => item !== key) : [...current.metrics, key] }));

  const appliedSummary = applied?.kind === "measurements" ? `${applied.filters.mode} · ${applied.filters.interval} · ${applied.filters.from} – ${applied.filters.to}` : "";

  return (
    <div className="h-full min-h-0 overflow-y-auto tp-scroll mx-auto w-full max-w-[1600px] animate-fade-in pr-1">
      <PageHeader title="Explorador de datos" subtitle="Consultas de clientes, dispositivos y mediciones de plataforma" testId="admin-explorer-header" />
      <Panel className="mb-4 p-4" testId="admin-explorer-builder">
        <div className="flex items-start gap-2"><Database size={17} className="mt-0.5 text-brand" /><div><Overline>Constructor de consultas</Overline><p className="mt-1 text-xs text-ink-secondary">Elige una vista y sus filtros. Los datos se consultan en el servidor con tu sesión de administrador.</p></div></div>
        {lookupError && <p className="mt-3 rounded-md border border-critical/30 bg-critical/5 px-3 py-2 text-xs text-critical">No se pudieron cargar los selectores: {lookupError}</p>}
        <div className="mt-4 flex flex-wrap gap-2" role="tablist" aria-label="Tipo de consulta">
          {[["customers", "Clientes"], ["devices", "Dispositivos"], ["measurements", "Mediciones"]].map(([key, label]) => <button key={key} type="button" role="tab" aria-selected={dataset === key} onClick={() => selectDataset(key)} className={`h-9 rounded-md px-4 text-sm font-semibold ${dataset === key ? "bg-brand text-white" : "border border-line text-ink-secondary hover:bg-page"}`}>{label}</button>)}
        </div>

        {dataset === "customers" && <form onSubmit={runQuery} className="mt-4 grid gap-3 md:grid-cols-4">
          <label><span className={labelClass}>Buscar cliente</span><input className={inputClass} value={customerFilters.search} onChange={(event) => setCustomerFilters((current) => ({ ...current, search: event.target.value }))} placeholder="Nombre" /></label>
          <label><span className={labelClass}>Más de X dispositivos</span><input type="number" min="0" className={inputClass} value={customerFilters.minDevicesGt} onChange={(event) => setCustomerFilters((current) => ({ ...current, minDevicesGt: event.target.value }))} placeholder="Sin mínimo" /></label>
          <label><span className={labelClass}>Contar</span><select className={inputClass} value={customerFilters.countScope} onChange={(event) => setCustomerFilters((current) => ({ ...current, countScope: event.target.value }))}><option value="all">Todos los dispositivos</option><option value="deployed">Dispositivos desplegados</option></select></label>
          <div className="flex items-end"><button className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-brand px-4 text-sm font-semibold text-white"><Search size={15} />Consultar</button></div>
        </form>}

        {dataset === "devices" && <form onSubmit={runQuery} className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <label><span className={labelClass}>Cliente</span><select className={inputClass} value={deviceFilters.tenantId} onChange={(event) => setDeviceFilters((current) => ({ ...current, tenantId: event.target.value, fieldId: "" }))}><option value="">Todos los clientes</option>{tenants.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.name}</option>)}</select></label>
          <label><span className={labelClass}>Parcela actual</span><select className={inputClass} value={deviceFilters.fieldId} onChange={(event) => setDeviceFilters((current) => ({ ...current, fieldId: event.target.value }))} disabled={!deviceFilters.tenantId || fieldsLoading}><option value="">Todas las parcelas</option>{fields.map((field) => <option key={field.id} value={field.id}>{field.name}</option>)}</select></label>
          <label><span className={labelClass}>Estado</span><select className={inputClass} value={deviceFilters.lifecycleState} onChange={(event) => setDeviceFilters((current) => ({ ...current, lifecycleState: event.target.value }))}><option value="">Todos</option>{["manufactured", "shipped", "provisioned", "assigned", "active", "suspended", "retired"].map((state) => <option key={state}>{state}</option>)}</select></label>
          <label><span className={labelClass}>ID o número de serie</span><input className={inputClass} value={deviceFilters.search} onChange={(event) => setDeviceFilters((current) => ({ ...current, search: event.target.value }))} /></label>
          <div className="flex items-end"><button className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-brand px-4 text-sm font-semibold text-white"><Search size={15} />Consultar</button></div>
        </form>}

        {dataset === "measurements" && <form onSubmit={runQuery} className="mt-4 space-y-4">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <label><span className={labelClass}>Cliente *</span><select name="tenantId" className={inputClass} value={measurementFilters.tenantId} onChange={updateMeasurement}><option value="">Selecciona un cliente</option>{tenants.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.name}</option>)}</select></label>
            <label><span className={labelClass}>Parcela</span><select name="fieldId" className={inputClass} value={measurementFilters.fieldId} onChange={updateMeasurement} disabled={!measurementFilters.tenantId || fieldsLoading}><option value="">Todas las parcelas</option>{fields.map((field) => <option key={field.id} value={field.id}>{field.name}</option>)}</select></label>
            <label><span className={labelClass}>ID de dispositivo (opcional)</span><input name="deviceId" className={inputClass} value={measurementFilters.deviceId} onChange={updateMeasurement} placeholder="tpt_dev_…" /></label>
            <label><span className={labelClass}>Resultado</span><select name="mode" className={inputClass} value={measurementFilters.mode} onChange={updateMeasurement}><option value="sampled">Última muestra por intervalo y dispositivo</option><option value="aggregate">Agregados por intervalo</option><option value="raw">Todas las filas originales</option></select></label>
            <label><span className={labelClass}>Desde *</span><input type="datetime-local" name="from" className={inputClass} value={measurementFilters.from} onChange={updateMeasurement} /></label>
            <label><span className={labelClass}>Hasta (exclusivo) *</span><input type="datetime-local" name="to" className={inputClass} value={measurementFilters.to} onChange={updateMeasurement} /></label>
            <label><span className={labelClass}>Intervalo</span><select name="interval" className={inputClass} value={measurementFilters.interval} onChange={updateMeasurement} disabled={measurementFilters.mode === "raw"}>{[["1m", "1 minuto"], ["10m", "10 minutos"], ["15m", "15 minutos"], ["1h", "1 hora"], ["1d", "1 día"]].map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>
            {measurementFilters.mode === "aggregate" && <label><span className={labelClass}>Agrupar</span><select name="groupBy" className={inputClass} value={measurementFilters.groupBy} onChange={updateMeasurement}><option value="device">Por dispositivo</option><option value="field" disabled={!measurementFilters.fieldId}>Por parcela (media de dispositivos)</option></select></label>}
            {measurementFilters.mode !== "aggregate" && <label><span className={labelClass}>Calidad</span><select name="quality" className={inputClass} value={measurementFilters.quality} onChange={updateMeasurement}><option value="valid">Solo mediciones válidas</option><option value="all">Todos los estados (diagnóstico)</option></select></label>}
          </div>
          <fieldset><legend className={labelClass}>Mediciones disponibles</legend><div className="flex flex-wrap gap-x-5 gap-y-2">{metrics.map((metric) => <label key={metric.key} className="flex items-center gap-2 text-xs text-ink-secondary"><input type="checkbox" checked={measurementFilters.metrics.includes(metric.key)} onChange={() => toggleMetric(metric.key)} /><span>{METRIC_LABELS[metric.key] || metric.label}</span><span className="text-ink-tertiary">· Sensor {metric.source_sensor_role === "leaf" ? "de hoja" : "de aire"}</span></label>)}</div></fieldset>
          <div className="flex flex-wrap items-end justify-between gap-3 border-t border-line pt-3"><p className="text-xs text-ink-tertiary">Fechas interpretadas en la zona horaria local ({Intl.DateTimeFormat().resolvedOptions().timeZone || "navegador"}) y enviadas en UTC.</p><button className="inline-flex h-10 items-center gap-2 rounded-md bg-brand px-4 text-sm font-semibold text-white"><Filter size={15} />Consultar datos</button></div>
        </form>}
      </Panel>

      {error && <div className="mb-3 rounded-md border border-critical/30 bg-critical/5 px-3 py-2 text-sm text-critical" role="alert">{error}</div>}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="text-sm font-semibold text-ink">Resultados</h2>{appliedSummary && <p className="mt-1 text-xs text-ink-secondary">{appliedSummary} · {results.data.length.toLocaleString("es-ES")} filas</p>}</div>
        {applied?.kind === "measurements" && <button type="button" onClick={() => void exportCsv()} disabled={exporting || loading} className="inline-flex h-9 items-center gap-2 rounded-md border border-line px-3 text-xs font-semibold text-brand disabled:opacity-50">{exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}Descargar CSV</button>}
      </div>
      <ResultsTable data={results.data} loading={loading} error="" nextOffset={results.next_offset} truncated={results.truncated} onLoadMore={() => void loadMore()} loadingMore={loadingMore} emptyText={applied ? "La consulta no devolvió filas." : "Configura una consulta y ejecútala para ver resultados."} />
    </div>
  );
}
